import { toast } from "sonner";
import { FileCheck2, Upload } from "lucide-react";
import { uploadArtwork } from "@/components/signs/useSignConfig";
import Mockup from "./mockups/Mockup";

const EMPTY = { file: null, url: null, busy: false, pan: { x: 0, y: 0 }, fit: "contain" };
export const emptySides = () => ({ front: { ...EMPTY }, back: { ...EMPTY } });

const toDataUrl = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = () => res(null); r.readAsDataURL(file); });

/** Put a file on one side: an image shows at once as the proof (as a data URL, so the proof capture can draw it); a PDF / AI / EPS shows as a received file. */
export async function uploadSide(setSides, which, file) {
    if (!file) return;
    const url = file.type.startsWith("image/") ? await toDataUrl(file) : null;
    const patch = (p) => setSides((prev) => ({ ...prev, [which]: { ...prev[which], ...p } }));
    patch({ file: null, url, busy: true, name: file.name });
    try { patch({ file: await uploadArtwork(file), busy: false }); }
    catch { patch({ ...EMPTY }); toast.error("Upload failed. Try again."); }
}

/**
 * The proof stage (Phase 10 / 11.2 rule 3): the product, full screen, with the customer's file on it. Upload only —
 * nothing to drag, scale or edit. Front / Back tabs float over it when the product prints on two sides.
 */
export default function ProofStage({ sides, view, setView, twoSided, mockup, size, window: win, folds, stageRef, onUpload, onUseFront, theme }) {
    const which = twoSided ? view : "front";
    const side = sides[which];
    return (
        <div ref={stageRef} className="relative h-full w-full" style={{ background: "#eef0f3" }} data-testid="print-stage" data-side={which}>
            <Mockup kind={mockup} url={side.url} fit="contain" pan={{ x: 0, y: 0 }} size={size} side={which} window={win} folds={folds} className="block h-full w-full" />

            {twoSided && (
                <div className="absolute left-4 top-4 z-10 flex rounded-full border border-zinc-200 bg-white p-0.5 shadow-sm" data-testid="side-tabs" data-html2canvas-ignore="true">
                    {["front", "back"].map((s) => (
                        <button key={s} type="button" onClick={() => setView(s)} data-testid={`side-tab-${s}`}
                            className="rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider transition-colors"
                            style={view === s ? { background: theme.primary, color: theme.on } : { color: "#71717a" }}>
                            {s}{sides[s].file ? " •" : ""}
                        </button>
                    ))}
                </div>
            )}
            <span className="absolute right-4 top-4 z-10 rounded-full bg-white/90 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 shadow-sm" data-testid="stage-caption" data-html2canvas-ignore="true">
                {size || "proof"}{twoSided ? ` · ${which}` : ""}
            </span>

            {side.file && !side.url && (
                <div className="absolute left-1/2 top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-sm font-semibold text-zinc-900 shadow" data-testid="print-file-received" data-html2canvas-ignore="true">
                    <FileCheck2 size={16} style={{ color: theme.primary }} /> {side.name || "File received"} — we print it as sent
                </div>
            )}

            {!side.url && !side.file && (
                <div className="absolute inset-x-0 bottom-6 z-10 flex flex-col items-center gap-2 px-6" data-html2canvas-ignore="true">
                    <button type="button" onClick={onUpload} disabled={side.busy} data-testid="upload-file"
                        className="inline-flex min-h-[48px] items-center gap-2 rounded-full px-6 text-sm font-bold uppercase tracking-wide shadow-lg transition-transform hover:-translate-y-px disabled:opacity-60"
                        style={{ background: theme.primary, color: theme.on || "#fff" }}>
                        <Upload size={16} /> {side.busy ? "Uploading…" : `Upload your file${twoSided ? ` · ${which}` : ""}`}
                    </button>
                    {twoSided && which === "back" && sides.front.url && (
                        <button type="button" onClick={onUseFront} className="text-xs font-semibold text-zinc-700 underline" data-testid="use-front-image">Use the front image</button>
                    )}
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">PDF · PNG · JPG · AI · EPS — your file is the proof</span>
                </div>
            )}
            {(side.url || side.file) && (
                <button type="button" onClick={onUpload} className="absolute bottom-4 right-4 z-10 rounded-full border border-zinc-300 bg-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-zinc-700 shadow-sm hover:border-zinc-900" data-testid="replace-file" data-html2canvas-ignore="true">
                    Replace file
                </button>
            )}
        </div>
    );
}
